// import { Animal } from "../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/GQLCalls/getAnimalCall";
// import { useAnimalConsumptionById, useAnimalConsumptionByMultipleIds } from "../../Services/Agrisys_GqlDataCaller/ConsumptionCalls/consumptionSlice";

import { useEffect, useMemo } from "react";
import { useAnimalConsumptionById, useAnimalConsumptionByMultipleIds, dispatchFetchByFilter, selectConsumptionById } from "../../Services/Agrisys_GqlDataCaller/ConsumptionCalls/consumptionSlice";
import { isEqual } from "lodash";
import { useAppDispatch, useAppSelector } from "../../Store";

type DayConsumption = {
  target_id: number;
  cnt: number;
  feed: {
    total: number;
    remaning: number;
  }
  water: {
    total: number;
    remaning: number;
  };
};

const aggregateConsumptions = (
  entries: { target_id: number; consumptions?: any[] }[]
): Record<number, DayConsumption> => {

  const agg = entries.reduce((acc, { target_id, consumptions }) => {
    consumptions?.forEach(consumption => {
      const componentId = consumption?.componentId;
      const milestoneDayOffset = (consumption.milestoneDayOffset ?? 0) * -1;

      if (consumption.milestoneDayOffset === undefined) return;

      if (!acc[milestoneDayOffset]) {
        acc[milestoneDayOffset] = {
          target_id,
          cnt: 0,
          feed: { total: 0, remaning: 0 },
          water: { total: 0, remaning: 0 }
        };
      }

      if (
        consumption.total === undefined ||
        consumption.progressed === undefined ||
        (consumption.progressed === 0 && consumption.total === 0)
      ) return;

      // Water
      if (componentId === 1) {
        acc[milestoneDayOffset].water.total += consumption.total ?? 0;
        acc[milestoneDayOffset].water.remaning += consumption.progressed ?? 0;
        return;
      }

      acc[milestoneDayOffset].cnt++;
      acc[milestoneDayOffset].feed.total += consumption.total ?? 0;
      acc[milestoneDayOffset].feed.remaning += consumption.progressed ?? 0;
    });

    return acc;
  }, {} as Record<number, DayConsumption>);

  // Fallback
  const fallback: DayConsumption = {
    target_id: -1,
    cnt: 0,
    feed: { total: 0, remaning: 0 },
    water: { total: 0, remaning: 0 }
  };

  for (let i = 0; i <= 7; i++) {
    if (!agg[i]) {
      agg[i] = { ...fallback };
    }
  }

  return agg;
};

export const useMultipleConsumptions = (animals: number[]) => {
  const consumptionEntities = useAnimalConsumptionByMultipleIds(
    animals.map(a => a.toString())
  );

  const entries = Object.entries(consumptionEntities)
    .filter(([_, entity]) => !entity.isLoading && entity.entity !== undefined)
    .map(([animalId, entityConsumption]) => ({
      target_id: Number(animalId),
      consumptions: entityConsumption.entity?.consumptions
    }));

  return aggregateConsumptions(entries);
};

export const useSingleConsumption = (animal: number) => {
  const { entity } = useAnimalConsumptionById(animal.toString());

  const entries = entity
    ? [{ target_id: animal, consumptions: entity.consumptions }]
    : [];

  return aggregateConsumptions(entries);
};

export function useFeedByLocation(locationId: number, pollingTime?: number) {

  const targetId = `location_id=${locationId}`
  const dispatch = useAppDispatch();

  useEffect(() => {
    const fetchData = () => {
      dispatch(dispatchFetchByFilter([{ key: "location_id", ids: [locationId] }], true))
    }

    fetchData();

    let interval = undefined;
    if (pollingTime) {
      interval = setInterval(fetchData, pollingTime);
    }

    return () => { if (interval !== undefined) { clearInterval(interval) } }
  }, [dispatch, locationId, pollingTime])

  const entity = useAppSelector((state) => selectConsumptionById(state, targetId), isEqual);
  const grouped = useMemo(() => {
    if (!entity) {
      return {
        today: { total: 0, progressed: 0 },
        yesterday: { total: 0, progressed: 0 },
      };
    }

    const todayEntity = entity.consumptions.filter(
      (c) => c.milestoneDayOffset === 0 && c.componentId !== 1
    );
    const yesterDayEntity = entity.consumptions.filter(
      (c) => c.milestoneDayOffset === -1 && c.componentId !== 1
    );

    const reduce = (list: typeof entity.consumptions) =>
      list.reduce(
        (agg, c) => {
          agg.total += c.total ?? 0;
          agg.progressed += c.progressed ?? 0;
          return agg;
        },
        { total: 0, progressed: 0 } as { total: number, progressed: number }
      );

    return {
      today: reduce(todayEntity),
      yesterday: reduce(yesterDayEntity),
    };
  }, [entity]);
  return grouped;
}
